import { useMemo } from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { actions } from '../../actions';
import { useFirestore } from 'react-redux-firebase';
import collections from '../../constants/FirestoreCollections';
import { IOrganization } from '../../Interfaces/IOrganization';

interface Props {
  closeMenu: () => {};
  color: string;
  isTransparent: boolean;
}

const useStyles = (color: string, isTransparent: boolean) =>
  makeStyles((theme: any) => ({
    selectRef: {
      fontSize: theme.sizes.sm.t1,
      color: theme.palette[color].main,
      width: '100%',
    },
    select: {
      width: '100%',
      minHeight: 30,
      marginInline: 16,
      padding: '12px 0px',
      borderRadius: 8,
      border: '1px solid white',
      backgroundColor: isTransparent ? 'transparent' : '',
      outline: 'none',
      color: theme.palette[color].main,
      // Estilos barra de scroll
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(0,0,0,0.2) transparent',
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
      },
    },
    selectItem: {
      color: theme.palette.black.main,
    },
  }));

function ClientSelector(props: Props) {
  const { closeMenu, color = 'white', isTransparent = true } = props;
  const classes = useStyles(color, isTransparent)();
  const organizations = useSelector((state: any) => state.app.organizations);
  const config = useSelector((state: any) => state.app.prestobotsConfig);
  const appDispatch = useDispatch();
  const firestore = useFirestore();
  const profile = useSelector((state: any) => state.firebase.profile);

  const handleSubmit = (e: any) => {
    const organizationId = e.target.value;
    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 1 },
    });
    closeMenu();

    if (organizationId) {
      firestore
        .collection(collections.users)
        .doc(profile.token.claims.user_id)
        .update({ organizationId: organizationId });
    }
  };

  const getPriority = (serviceName: string): number => {
    try {
      const servicePriorityOrder = config?.servicePriorityOrder;
      return servicePriorityOrder?.[serviceName] ?? Number.MAX_SAFE_INTEGER;
    } catch (error) {
      console.error('getPriority', error);
      return Number.MAX_SAFE_INTEGER;
    }
  };
  const sortedCategories = useMemo(() => {
    if (!organizations) return [];

    const activeOrganizations = organizations
      .filter((e: IOrganization) => e.disabled !== true)
      .sort((a: { name: string | undefined }, b: { name: string | undefined }) => {
        const nameA = a.name ?? '';
        const nameB = b.name ?? '';
        return nameA.localeCompare(nameB);
      });

    const grouped: { [category: string]: typeof activeOrganizations } = activeOrganizations.reduce(
      (acc: { [x: string]: any[] }, org: { healthSoftware: { name: string | undefined } }) => {
        const category = org?.healthSoftware?.name || 'Sin integración';
        if (!acc[category]) acc[category] = [];
        acc[category].push(org);
        return acc;
      },
      {},
    );

    const categoriesSortedByPriority = Object.entries(grouped).sort(([a], [b]) => {
      return getPriority(a) - getPriority(b);
    });

    return categoriesSortedByPriority;
  }, [organizations, config]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" px={2} mt={2}>
      <Typography className={classes.selectRef}>Seleccione un cliente</Typography>
      <select onChange={handleSubmit} value={profile.organizationId} className={classes.select}>
        {sortedCategories.map(([categoryName, organizations]: [string, any]) => (
          <optgroup
            key={categoryName}
            label={categoryName.toUpperCase()}
            style={{ color: '#4F4F4F', fontSize: '.9rem' }}
          >
            {organizations.map((item: any) => (
              <option className={classes.selectItem} key={item.id} value={item.id}>
                {item.name}
                {!item?.services?.healthSoftware ? ' (*)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </Box>
  );
}

export default ClientSelector;
